// PARA MIRAR — el catálogo CERRADO de señales.
//
// Hoy el dueño tiene que salir a cazar el problema: entra a Estadísticas, mira
// 35 gráficos y trata de darse cuenta solo de si algo se salió de lo normal.
// Este archivo invierte eso: la pantalla revisa y avisa.
//
// Cuatro reglas que valen para TODAS las señales, y que son la diferencia entre
// una alerta que se lee y una que se ignora a la semana:
//
// 1. NADA SE DISPARA POR UN VALOR ABSOLUTO. Siempre contra la propia historia
//    del cliente. "12 anulaciones" no significa nada; "12 cuando venías en 3"
//    sí. Una playa de 400 cocheras y un garaje de 20 no comparten umbrales.
//
// 2. MEDIANA, NUNCA PROMEDIO. Un solo día extraordinario corre el promedio y
//    deja la vara mal puesta para todo el resto del mes.
//
// 3. EL TEXTO NIEGA LA CONCLUSIÓN FÁCIL. Anular no es robar; un movimiento
//    marcado no es una salida sin cobrar. Si la señal acusa, el cliente que la
//    recibe por un motivo legítimo deja de creerle — y entonces tampoco la lee
//    el día que sí importa.
//
// 4. SI NO HAY CON QUÉ COMPARAR, NO HAY SEÑAL. Con menos de MIN_HISTORIA días
//    cerrados no se emite nada: preferimos no decir nada a inventar una alarma.

const MIN_HISTORIA = 21; // tres semanas: menos que eso no es una línea base
const VENTANA = 7; // qué se mira: la última semana

/** Mediana — resistente al día extraordinario, que es de lo que se trata. */
const mediana = (nums) => {
  const o = nums.filter((n) => typeof n === "number" && isFinite(n)).sort((a, b) => a - b);
  if (!o.length) return null;
  const m = Math.floor(o.length / 2);
  return o.length % 2 ? o[m] : (o[m - 1] + o[m]) / 2;
};

/**
 * Desviación absoluta mediana. Es a la mediana lo que el desvío estándar es al
 * promedio, pero sin que un valor extremo infle la vara y termine tapando justo
 * el caso que queremos detectar.
 */
const mad = (nums, med) => mediana(nums.map((n) => Math.abs(n - med)));

/**
 * ¿`valor` se sale de lo normal de `base`?
 * El piso de 1 evita el caso degenerado: si el cliente SIEMPRE tuvo 0, la
 * dispersión da 0 y cualquier valor mayor a cero se volvería infinitamente
 * anormal.
 */
const seSale = (valor, base, factor = 3) => {
  const med = mediana(base);
  if (med === null) return null;
  const disp = Math.max(mad(base, med) || 0, 1);
  return { med, z: (valor - med) / disp, anormal: (valor - med) / disp >= factor };
};

const sumar = (docs, f) => docs.reduce((a, d) => a + (f(d) || 0), 0);

const diaDeSemana = (iso) => new Date(`${iso}T00:00:00`).getDay();

// ── Las señales ──────────────────────────────────────────────────────────

/** 1 · Anulaciones fuera de lo normal. */
const anulaciones = ({ recientes, previos }) => {
  const canc = sumar(recientes, (d) => d.canceled_count);
  if (!canc) return null;
  const r = seSale(canc / VENTANA, previos.map((d) => d.canceled_count || 0));
  if (!r || !r.anormal) return null;
  return {
    id: "anulaciones",
    nivel: "mirar",
    titular: `Anulaste ${canc} tickets esta semana, más de lo habitual`,
    detalle: `Lo habitual en tu playa son alrededor de ${Math.round(r.med)} por día y esta semana promediaste ${(canc / VENTANA).toFixed(1)}.`,
    // Sin esta línea la señal acusa, y el cliente que anula por motivos
    // legítimos deja de creerle a la pantalla.
    matiz: "Anular un ticket no es necesariamente un problema: puede ser un error de carga corregido, o un vehículo que volvió a entrar.",
    verEn: { texto: "Ver los tickets", ruta: "/tickets" },
  };
};

/** 2 · Movimientos marcados. NUNCA se rotula "salidas sin cobrar". */
const marcados = ({ recientes, previos }) => {
  const n = sumar(recientes, (d) => d.alerts_count);
  if (!n) return null;
  const r = seSale(n / VENTANA, previos.map((d) => d.alerts_count || 0));
  if (!r || !r.anormal) return null;
  return {
    id: "marcados",
    nivel: "mirar",
    titular: `${n} movimientos marcados esta semana, más de lo habitual`,
    detalle: `Lo normal en tu playa es cerca de ${Math.round(r.med)} por día.`,
    // El catálogo de tc_qml mezcla severidades e incluye "Reasignado" y
    // "Salida descartada", que una playa con ANPR genera legítimamente todos
    // los días. Rotularlo "salidas sin cobrar" sería una acusación falsa.
    matiz: "Son movimientos que el sistema marcó para revisar, no necesariamente errores. Una playa con lectura de patentes genera algunos todos los días.",
    verEn: { texto: "Ver Entradas / Salidas", ruta: "/inout" },
  };
};

/** 3 · Caída sostenida: contra los MISMOS días de la semana, no contra el resto. */
const caida = ({ recientes, cerrados }) => {
  if (recientes.length < 5) return null;

  const porDow = {};
  cerrados.forEach((d) => {
    if (!d.date) return;
    const k = diaDeSemana(d.date);
    (porDow[k] = porDow[k] || []).push(d.income || 0);
  });

  // Un día en el que no entró ni un peso ni un auto es un día que la playa no
  // abrió, y un día cerrado NO es un día flojo: no vale $0, no vale nada. Es la
  // misma regla que usan `TusDias` y `Lo que salta a la vista`, y sin ella esta
  // señal se disparaba sola. Peor: al que cerró tres días esta pantalla le
  // ponía "facturaste 30% menos de lo habitual" en rojo arriba, y dos bloques
  // más abajo "4 días seguidos por encima de lo habitual" en verde —porque la
  // racha sí descarta los días cerrados—. La pantalla se desmentía sola.
  const opero = (d) => (Number(d?.income) || 0) > 0 || (Number(d?.tickets?.total?.in) || 0) > 0;
  const evaluables = recientes.filter((d) => d.date && opero(d));
  if (evaluables.length < 5) return null;

  let peores = 0;
  let esperado = 0;
  let real = 0;
  evaluables.forEach((d) => {
    // Los últimos 7 de ese día, sin contar la semana que estamos evaluando.
    const hist = (porDow[diaDeSemana(d.date)] || []).slice(0, -1).slice(-7).filter((v) => v > 0);
    if (hist.length < 4) return;
    const med = mediana(hist);
    esperado += med;
    real += d.income || 0;
    if ((d.income || 0) < med * 0.75) peores += 1;
  });

  if (peores < 4 || !esperado) return null;
  const pct = Math.round(((esperado - real) / esperado) * 100);
  if (pct < 15) return null;

  return {
    id: "caida",
    nivel: "aviso",
    titular: `Esta semana facturaste ${pct}% menos de lo habitual`,
    detalle: `${peores} de los últimos ${evaluables.length} días con movimiento quedaron bastante por debajo de lo que suele dejarte ese mismo día de la semana.`,
    matiz: "Se compara cada día contra los mismos días de semanas anteriores, así que no es el efecto de un fin de semana o un feriado.",
    verEn: null,
  };
};

/** 4 · Abonados con saldo pendiente. Viene de Redux, no de estadísticas. */
const deudaAbonados = ({ abonados }) => {
  if (!Array.isArray(abonados) || !abonados.length) return null;
  const deudores = abonados.filter((c) => (c?.balance || 0) < 0);
  if (!deudores.length) return null;
  const total = deudores.reduce((a, c) => a + Math.abs(c.balance || 0), 0);
  if (total <= 0) return null;
  return {
    id: "deuda",
    nivel: "mirar",
    titular: `${deudores.length} ${deudores.length === 1 ? "abonado tiene" : "abonados tienen"} saldo pendiente`,
    monto: total,
    detalle: "",
    matiz: "Incluye a quienes todavía están dentro de su plazo de pago.",
    verEn: { texto: "Ver abonados", ruta: "/cc" },
  };
};

/** 5 · Días sin datos: la playa cerró, o la PC no envió. No sabemos cuál. */
const sinDatos = ({ cerrados }) => {
  const hay = new Set(cerrados.map((d) => d.date));
  const faltan = [];
  const hoy = new Date();
  for (let i = 1; i <= 30; i++) {
    const d = new Date(hoy);
    d.setDate(d.getDate() - i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (!hay.has(iso)) faltan.push(iso);
  }
  if (faltan.length < 3) return null;
  return {
    id: "sin-datos",
    nivel: "info",
    titular: `${faltan.length} de los últimos 30 días no tienen datos`,
    detalle: "Esos días no entran en ningún promedio ni en ninguna comparación de esta pantalla.",
    // No se puede distinguir "cerré" de "la PC no envió" con lo que llega, y
    // afirmar cualquiera de las dos sería inventar.
    matiz: "Puede ser que la playa haya estado cerrada, o que TicketControl no haya podido enviar esos días.",
    verEn: null,
  };
};

const CATALOGO = [caida, anulaciones, marcados, deudaAbonados, sinDatos];

/**
 * Evalúa todas las señales.
 *
 * Devolver una lista vacía NO es un caso degenerado: es información. Significa
 * "revisé y está todo dentro de lo normal", y así se muestra en pantalla.
 */
export const evaluarSenales = ({ cerrados = [], abonados = [] }) => {
  if (cerrados.length < MIN_HISTORIA) {
    return { senales: [], suficiente: false, faltan: MIN_HISTORIA - cerrados.length };
  }

  const ctx = {
    cerrados,
    recientes: cerrados.slice(-VENTANA),
    previos: cerrados.slice(0, -VENTANA),
    abonados,
  };

  const senales = CATALOGO
    .map((f) => {
      // Una regla que falla no puede tumbar el bloque entero ni, peor, dejar la
      // pantalla en blanco: se pierde esa señal y las demás siguen.
      try { return f(ctx); } catch { return null; }
    })
    .filter(Boolean);

  const ORDEN = { aviso: 0, mirar: 1, info: 2 };
  senales.sort((a, b) => ORDEN[a.nivel] - ORDEN[b.nivel]);
  return { senales, suficiente: true, faltan: 0 };
};

export const MINIMO_HISTORIA = MIN_HISTORIA;

// La ventana evaluada, para que el bloque pueda declarar el rango REAL de fechas
// que miró. Si se exportara el número a mano en la pantalla, el día que VENTANA
// cambie el rango mentiría sin que nadie se entere.
export const VENTANA_SENALES = VENTANA;
